import { getConnector } from '../services/PatientFacingClient';
import { RootDispatch, RootState } from '../store';

export interface MessagesState {
  [key: string]: string;
}
const initialState: MessagesState = {};

let createMessageTimerID: number;
let newMessagesBuffer: MessagesState = {};
let updateMessageTimerID: number;
let updateMessagesBuffer: MessagesState = {};

export const messages = {
  effects: (dispatch: RootDispatch) => ({
    removeMessage({ id }: { id: string }, rootState: RootState) {
      if (id in rootState.messages) {
        dispatch.messages.remove(id);
      }
    },
    createMessage(
      {
        $lastModified,
        conversationId,
        id,
        inTimeline,
      }: {
        $lastModified: string;
        id: string;
        conversationId: string;
        inTimeline: boolean;
      },
      rootState: RootState
    ) {
      if (inTimeline && conversationId === rootState.conversation.id) {
        const { client } = getConnector();
        const { higherContinuation } = client.conversations.getById(rootState.conversation.id);
        Object.assign(newMessagesBuffer, { [id]: $lastModified });
        clearTimeout(createMessageTimerID);
        //eslint-disable-next-line scanjs-rules/call_setTimeout
        createMessageTimerID = setTimeout(() => {
          dispatch.messages.create(newMessagesBuffer);

          if (rootState.messageLoad.loading) {
            dispatch.messageLoad.stopMessageLoader();
          } else if (higherContinuation.itemsEstimate) {
            return;
          } else {
            dispatch.messageLoad.initialLoadComplete();
          }
        }, 50);
      }
    },
    updateMessage(
      {
        $lastModified,
        conversationId,
        id,
        inTimeline,
      }: {
        $lastModified: string;
        id: string;
        conversationId: string;
        inTimeline: boolean;
      },
      rootState: RootState
    ) {
      if (inTimeline && conversationId === rootState.conversation.id) {
        if (rootState.messages[id] && rootState.messages[id] === $lastModified) {
          return;
        }

        const { client } = getConnector();
        const message = client.messages.getById(id);

        if (message?.vwrCallInvite?.status) {
          if (message.vwrCallInvite.status === 'invite') {
            const { vwrCallInvite, ttl } = message;
            dispatch.virtualWaitingRoom.handleVWRCallEvent({
              ...vwrCallInvite,
              receivedAt: new Date(message.createdAt),
              status: vwrCallInvite.status,
              ttl,
            });
          } else {
            dispatch.virtualWaitingRoom.clearCall();
          }
        }

        Object.assign(updateMessagesBuffer, { [id]: $lastModified });
        clearTimeout(updateMessageTimerID);
        //eslint-disable-next-line scanjs-rules/call_setTimeout
        updateMessageTimerID = setTimeout(() => {
          dispatch.messages.update(updateMessagesBuffer);
          updateMessagesBuffer = {};
        }, 100);
      }
    },
    async sendMessage(
      { body, options }: { body: string; options: Object | null },
      rootState: RootState
    ) {
      try {
        const { client } = getConnector();
        const message = await client.messages.sendToConversation(
          rootState.conversation.id,
          body,
          options
        );
        return message;
      } catch (err) {
        console.error(err);
        return null;
      }
    },
    async recallMessage(messageId: string) {
      try {
        const { client } = getConnector();
        await client.messages.recall(messageId);
        dispatch.messages.remove(messageId);
      } catch (err) {
        console.error(err);
        return;
      }
    },
    async retryMessage(messageId: string) {
      try {
        const { client } = getConnector();
        await client.messages.retrySend(messageId);
      } catch (err) {
        console.error(err);
        return;
      }
    },
    async getMessages(direction: string, rootState: RootState) {
      const { client } = getConnector();
      const conversation = client.conversations.getById(rootState.conversation.id);
      if (!conversation) {
        console.error('No conversation found');
        return;
      }
      const { lowerContinuation, higherContinuation } = conversation;
      let continuation;
      if (lowerContinuation.itemsEstimate === 0 && higherContinuation.itemsEstimate === 0) {
        return;
      }
      if (direction === 'TOP' && lowerContinuation.itemsEstimate > 0) {
        continuation = lowerContinuation.continuation;
      } else if (direction === 'BOTTOM' && higherContinuation.itemsEstimate > 0) {
        continuation = higherContinuation.continuation;
      } else return;

      dispatch.messageLoad.startMessageLoader(direction);
      await client.conversations.fetchTimeline(rootState.conversation.id, {
        anchorPoint: 'CONTINUATION',
        continuation,
      });
      dispatch.messageLoad.stopMessageLoader();
    },
  }),
  reducers: {
    remove(state: MessagesState, id: string): MessagesState {
      const newState = Object.assign({}, state);
      delete newState[id];
      return newState;
    },
    create(state: MessagesState, messages: any) {
      return Object.assign({}, state, messages);
    },
    update(state: MessagesState, messages: any) {
      return Object.assign({}, state, messages);
    },
  },
  state: initialState,
};
